import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CanDeactivateFn } from '@angular/router';
import { map } from 'rxjs';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
  ConfirmDialogResult,
} from '../../admin/shared/components/confirm-dialog/confirm-dialog.component';

/** Implemented by components that hold edits worth warning about before navigation. */
export interface HasUnsavedChanges {
  hasUnsavedChanges(): boolean;
}

/**
 * Blocks navigation away from a component with unsaved edits until the user confirms.
 *
 * Uses the shared confirm dialog rather than `window.confirm` so the prompt matches the rest of
 * the admin screens. Tab and window closes are a separate path the browser owns — components
 * cover those with their own `beforeunload` handler.
 */
export const unsavedChangesGuard: CanDeactivateFn<HasUnsavedChanges> = (component) => {
  if (!component.hasUnsavedChanges()) {
    return true;
  }

  return inject(MatDialog)
    .open<ConfirmDialogComponent, ConfirmDialogData, ConfirmDialogResult>(ConfirmDialogComponent, {
      width: '28rem',
      data: {
        title: 'Discard unsaved changes?',
        message: 'You have unsaved changes on this page. Leaving now will discard them.',
        confirmText: 'Discard',
        cancelText: 'Keep editing',
        confirmColor: 'warn',
        showWarningIcon: true,
      },
    })
    .afterClosed()
    .pipe(map((result) => !!result?.confirmed));
};
